import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  PermissionGuard,
  NoPermissionFallback,
} from "../../components/PermissionGuard";
import { User, ApiKeyOption, ApiKeyResponse } from "../../interfaces/user";

const UserEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [selectedApiKey, setSelectedApiKey] = useState<string>("");
  const [apiKeys, setApiKeys] = useState<ApiKeyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // 載入用戶資料
  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;
      
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("未找到授權令牌");
        }

        const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com/";
        const response = await fetch(`${API_URL}/auth/users/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("載入用戶資料失敗");
        }

        const data = await response.json();
        setUser(data.data.user);
        setSelectedApiKey(data.data.user.apikey || "");
      } catch (err) {
        setError(err instanceof Error ? err.message : "載入失敗");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  // 載入可用的 API Keys
  useEffect(() => {
    const fetchApiKeys = async () => {
      setLoadingApiKeys(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com/";
        const response = await fetch(`${API_URL}/auth/available-apikeys`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data: ApiKeyResponse = await response.json();
          setApiKeys(data.data.apiKeys);
        }
      } catch (error) {
        console.error("載入 API Keys 失敗:", error);
      } finally {
        setLoadingApiKeys(false);
      }
    };

    fetchApiKeys();
  }, []);

  // 更新用戶 API Key
  const handleUpdateApiKey = async () => {
    if (!user?.id) return;

    setUpdating(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("未找到授權令牌");
      }

      const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com/";
      const response = await fetch(`${API_URL}/auth/users/${user.id}/apikey`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          apikey: selectedApiKey || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "更新失敗");
      }

      const data = await response.json();
      setUser(data.data.user);
      setSuccess(true);
      
      // 3秒後清除成功訊息
      setTimeout(() => setSuccess(false), 3000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : "更新失敗");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <PermissionGuard
        requiredRoles={["admin", "maintainer"]}
        fallback={<NoPermissionFallback />}
      >
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">載入錯誤</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard
      requiredRoles={["admin", "maintainer"]}
      fallback={<NoPermissionFallback />}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        {/* 面包屑導航 */}
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <div>
                <Link to="/users" className="text-gray-400 hover:text-gray-500">
                  <svg className="flex-shrink-0 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  <span className="sr-only">返回</span>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <Link to="/users" className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                  用戶管理
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <Link to={`/users/${id}`} className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                  {user?.name}
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <span className="ml-4 text-sm font-medium text-gray-500">編輯 API Key</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* 頁面標題 */}
        <div className="mt-6">
          <h1 className="text-2xl font-bold text-gray-900">編輯用戶 API Key</h1>
          <p className="mt-2 text-sm text-gray-600">
            管理用戶 "{user?.name}" 的 API Key 設定
          </p>
        </div>

        {/* 編輯表單 */}
        <div className="mt-8 max-w-lg">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              {success && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">API Key 更新成功！</p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 用戶資訊 */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">用戶資訊</h3>
                <dl className="grid grid-cols-1 gap-2 text-sm">
                  <div>
                    <dt className="font-medium text-gray-500">姓名：</dt>
                    <dd className="text-gray-900">{user?.name}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-500">電子郵件：</dt>
                    <dd className="text-gray-900">{user?.email}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-500">角色：</dt>
                    <dd className="text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user?.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        user?.role === 'maintainer' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {user?.role === 'admin' ? '管理員' :
                         user?.role === 'maintainer' ? '維護員' : '一般用戶'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-500">目前 API Key：</dt>
                    <dd className="text-gray-900 font-mono text-xs">
                      {user?.apikey || '未設定'}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* API Key 選擇 */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="apikey" className="block text-sm font-medium text-gray-700">
                    選擇 API Key
                  </label>
                  <div className="mt-1">
                    <select
                      id="apikey"
                      value={selectedApiKey}
                      onChange={(e) => setSelectedApiKey(e.target.value)}
                      disabled={loadingApiKeys}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">移除 API Key</option>
                      {apiKeys.map((option) => (
                        <option key={option.apiKey} value={option.apiKey}>
                          {option.projectName} ({option.apiKey.substring(0, 8)}...)
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {loadingApiKeys 
                      ? "載入中..."
                      : apiKeys.length > 0 
                        ? "選擇用戶可存取的專案 API Key，或選擇移除現有設定"
                        : "目前沒有可用的 API Key"
                    }
                  </p>
                </div>

                {/* 提交按鈕 */}
                <div className="flex justify-end space-x-3">
                  <Link
                    to={`/users/${id}`}
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    取消
                  </Link>
                  <button
                    type="button"
                    onClick={handleUpdateApiKey}
                    disabled={updating || loadingApiKeys}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        更新中...
                      </>
                    ) : (
                      "更新 API Key"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
};

export default UserEditPage;